import React, { useCallback } from 'react';
// aria-label: design-system-bypass 


// Haptic feedback utility
const triggerHaptic = (intensity: 'light' | 'medium' = 'light') => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(intensity === 'light' ? 10 : 20);
    }
};

interface CardProps {
    children: React.ReactNode;
    className?: string;
    variant?: 'default' | 'glass' | 'outline' | 'gradient' | 'glass-block' | 'liquid-glass' | 'organic' | 'glass-dark' | 'elevated' | 'none';
    hover?: boolean;
    padding?: 'none' | 'sm' | 'md' | 'lg';
    onClick?: () => void;
    /** Enable haptic feedback on click */
    haptic?: boolean;
    /** Accessibility label for interactive cards */
    'aria-label'?: string;
}

export function Card({
    children,
    className = '',
    variant = 'default',
    hover = false,
    padding = 'md',
    onClick,
    haptic = true,
    'aria-label': ariaLabel,
}: CardProps) {
    const baseClasses = 'card rounded-2xl transition-all duration-300';

    const variantClasses = {
        default: 'bg-white dark:bg-gray-800 shadow-lg border border-gray-100 dark:border-gray-700',
        glass: 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-xl border border-white/30 dark:border-gray-700/30',
        outline: 'bg-transparent border-2 border-gray-200 dark:border-gray-600',
        gradient: 'bg-gradient-to-br from-emerald-500/10 to-teal-500/10 dark:from-emerald-600/20 dark:to-teal-600/20 border border-emerald-200/50 dark:border-emerald-700/30',
        'glass-block': 'glass-block',
        'liquid-glass': 'liquid-glass',
        'organic': 'card-organic',
        'glass-dark': 'bg-gray-900/80 backdrop-blur-xl border border-white/10 text-white',
        'elevated': 'bg-white dark:bg-gray-800 shadow-xl shadow-black/5 border border-gray-100/50 dark:border-gray-700/50',
        'none': '',
    };

    const paddingClasses = {
        none: '',
        sm: 'p-3',
        md: 'p-5',
        lg: 'p-7',
    };

    const hoverClasses = hover || onClick
        ? 'hover:shadow-2xl hover:scale-[1.01] hover:border-emerald-300 dark:hover:border-emerald-600 cursor-pointer active:scale-[0.99]'
        : '';

    const handleClick = useCallback(() => {
        if (onClick) {
            if (haptic) triggerHaptic('light');
            onClick();
        }
    }, [onClick, haptic]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            if (haptic) triggerHaptic('light');
            onClick();
        }
    }, [onClick, haptic]);

    return (
        <div
            className={`${baseClasses} ${variantClasses[variant]} ${paddingClasses[padding]} ${hoverClasses} ${className}`}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            aria-label={ariaLabel}
        >
            {children}
        </div>
    );
}

interface CardHeaderProps {
    children: React.ReactNode;
    className?: string;
    icon?: React.ReactNode;
    action?: React.ReactNode;
}

export function CardHeader({ children, className = '', icon, action }: CardHeaderProps) {
    return (
        <div className={`card-header flex items-center justify-between mb-4 ${className}`}>
            <div className="flex items-center gap-3">
                {icon && (
                    <div className="card-icon p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/25" aria-hidden="true">
                        {icon}
                    </div>
                )}
                <div className="font-semibold text-lg text-gray-800 dark:text-gray-100">
                    {children}
                </div>
            </div>
            {action && <div className="card-action">{action}</div>}
        </div>
    );
}

interface CardContentProps {
    children: React.ReactNode;
    className?: string;
}

export function CardContent({ children, className = '' }: CardContentProps) {
    return (
        <div className={`card-content ${className}`}>
            {children}
        </div>
    );
}

interface CardFooterProps {
    children: React.ReactNode;
    className?: string;
}

export function CardFooter({ children, className = '' }: CardFooterProps) {
    return (
        <div className={`card-footer mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 ${className}`}>
            {children}
        </div>
    );
}

/**
 * CardSkeleton - Loading state placeholder for cards
 * Use this when loading card content
 */
interface CardSkeletonProps {
    /** Number of content lines to show */
    lines?: number;
    /** Show header with icon */
    showHeader?: boolean;
    /** Additional class name */
    className?: string;
}

export function CardSkeleton({
    lines = 3,
    showHeader = true,
    className = '',
}: CardSkeletonProps) {
    const shimmerClass = `
        relative overflow-hidden
        bg-gray-200 dark:bg-gray-700
        before:absolute before:inset-0
        before:-translate-x-full
        before:animate-[shimmer_1.5s_infinite]
        before:bg-gradient-to-r
        before:from-transparent
        before:via-white/20
        before:to-transparent
    `;

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-lg ${className}`} aria-hidden="true">
            {showHeader && (
                <div className="flex items-center gap-3 mb-4">
                    <div className={`${shimmerClass} w-10 h-10 rounded-xl`} />
                    <div className="flex-1 space-y-2">
                        <div className={`${shimmerClass} h-4 rounded w-3/4`} />
                        <div className={`${shimmerClass} h-3 rounded w-1/2`} />
                    </div>
                </div>
            )}
            <div className="space-y-3">
                {Array.from({ length: lines }).map((_, i) => (
                    <div
                        key={i}
                        className={`${shimmerClass} h-3 rounded`}
                        style={{ width: `${100 - i * 15}%` }}
                    />
                ))}
            </div>
        </div>
    );
}

export default Card;

